/**
 * native-review.ts — Apple/Google native in-app rating prompt at happy moments
 *
 * Wraps @capacitor-community/in-app-review (SKStoreReviewController on iOS,
 * Play In-App Review on Android). The OS decides whether the prompt actually
 * appears (Apple caps it at 3 per user per year) and never tells us the
 * outcome — so this is fire-and-forget.
 *
 * Gates (all localStorage, all wrapped so storage errors never throw):
 *   - native Capacitor app only (never web / Android TWA)
 *   - never within the first day after first launch on this install
 *   - only on a happy moment: a settle-up, or the 3rd+ expense added
 *   - at most once every 60 days
 *   - if the custom ReviewPromptSheet is switched on
 *     (CUSTOM_REVIEW_SHEET_ENABLED in lib/reviewPrompt.ts), never while it is
 *     still pending for this user, so the two rating asks never stack
 *
 * Safe to import on any platform — every function is a no-op off-native.
 */

import { Capacitor } from "@capacitor/core";
import { InAppReview } from "@capacitor-community/in-app-review";
import { CUSTOM_REVIEW_SHEET_ENABLED, shouldShowReview } from "@/lib/reviewPrompt";

export type ReviewTrigger = "settle_up" | "expense_added";

const K_FIRST_LAUNCH = "spliiit_nr_first_launch";
const K_LAST_ASKED = "spliiit_nr_last_asked";
const K_EXPENSES = "spliiit_nr_expenses_added";
const K_LAST_OPEN_ASK = "spliiit_nr_last_open_ask";

const DAY_MS = 24 * 60 * 60 * 1000;
const MIN_AGE_MS = DAY_MS;          // not within the first day
const COOLDOWN_MS = 60 * DAY_MS;    // at most once every 60 days
const HAPPY_EXPENSE_COUNT = 3;      // 3rd expense added = happy moment
const DELAY_MS = 1500;              // let the success toast land first
const DASH_DELAY_MS = 900;          // let the dashboard paint behind the prompt

function get(key: string): string | null {
  try { return localStorage.getItem(key); } catch { return null; }
}
function set(key: string, val: string) {
  try { localStorage.setItem(key, val); } catch {}
}
function getNum(key: string): number {
  const n = Number(get(key));
  return Number.isFinite(n) ? n : 0;
}

/** Stamp the first launch on this install. Call once on app start; idempotent. */
export function recordFirstLaunch() {
  if (!Capacitor.isNativePlatform()) return;
  if (!get(K_FIRST_LAUNCH)) set(K_FIRST_LAUNCH, String(Date.now()));
}

/**
 * Call right after a successful happy-moment action. Decides whether this
 * counts, checks the gates, then asks the OS for the native rating prompt
 * ~1.5s later. Never throws.
 */
export function maybeAskForReview(trigger: ReviewTrigger) {
  try {
    if (!Capacitor.isNativePlatform()) return;

    let happy = trigger === "settle_up";
    if (trigger === "expense_added") {
      const count = getNum(K_EXPENSES) + 1;
      set(K_EXPENSES, String(count));
      happy = count >= HAPPY_EXPENSE_COUNT;
    }
    if (!happy) return;

    const now = Date.now();
    const firstLaunch = getNum(K_FIRST_LAUNCH);
    if (!firstLaunch) { recordFirstLaunch(); return; }
    if (now - firstLaunch < MIN_AGE_MS) return;
    if (now - getNum(K_LAST_ASKED) < COOLDOWN_MS) return;
    if (CUSTOM_REVIEW_SHEET_ENABLED && shouldShowReview()) return;

    set(K_LAST_ASKED, String(now));
    setTimeout(() => {
      InAppReview.requestReview().catch((err) => {
        console.warn("[review] requestReview failed:", err);
      });
    }, DELAY_MS);
  } catch (err) {
    console.warn("[review] maybeAskForReview failed:", err);
  }
}

/**
 * True when Apple's native rating prompt can run here: the iOS app, on a build
 * that ships the plugin (1.3.4+). Older builds and Android/web return false.
 */
export function canAskNativeRating(): boolean {
  try {
    return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "ios" && Capacitor.isPluginAvailable("InAppReview");
  } catch {
    return false;
  }
}

let askedThisOpen = false;

/**
 * Every app open, once someone is past the intro, sign-in and first-run wizard:
 * ask for Apple's native rating prompt over the app. iOS decides whether it
 * actually shows (about 3 times a year per person) and stops once they rate,
 * so asking on each open is how we get it in front of as many people as Apple
 * allows. Leaves the happy-moment asks alone. Never throws.
 */
export function askForRatingOnOpen() {
  try {
    if (askedThisOpen || !canAskNativeRating()) return;
    askedThisOpen = true;
    set(K_LAST_OPEN_ASK, String(Date.now()));
    setTimeout(() => {
      InAppReview.requestReview().catch((err) => {
        console.warn("[review] on-open requestReview failed:", err);
      });
    }, DASH_DELAY_MS);
  } catch (err) {
    console.warn("[review] askForRatingOnOpen failed:", err);
  }
}
